import React, { useState, useEffect, useCallback, useMemo, useId } from "react";
import Icon from "../icons/Icon";
import Modal from "./Modal";
import {
  calculateWorkingHours,
  calculateEffectiveTimeIn,
  calculateEffectiveTimeOut,
  calculateOvertimeRestDay,
  calculateUndertime,
  calculateNightDiff,
  hasNDBreakAmbiguity,
  applyOtIncrement,
  calculateLateHours,
  createDateTime,
  buildScheduleDateTime,
  to24Hour,
  formatDisplayDate,
  formatDateString,
} from "../../utils/timeCalculations";
import { getDayName, isRestDayForEmployee } from "../../utils/restDayUtils";
import { isEmployeeActiveOnDate } from "../../utils/employeeFilters";
import { formatYMDForDisplay, manilaYMD, manilaHHMM } from "../../utils/periodDate";
import {
  saveTimeRecord,
  updateTimeRecord,
  checkHoliday,
  getActiveEmployees,
  validateTimeRecord,
} from "../../services/timeRecordService";
import { updateOtApprovalSummary } from "../../services/otApprovalSummaryService";
import { evaluateOtStatus } from "../../utils/otApprovalPolicy";
import { isHardLock, loadActiveLocks, deriveLockStatus } from "../../services/dtrLockDerivation";
import { getAttendancePolicy } from "../../services/attendancePolicyService";
import ConfirmModal from "./ConfirmModal";
import EmployeePicker from "../EmployeePicker";

// 24h "HH:MM" (a schedule time, or a native time field's value) → the form's 12h
// {hours, minutes, period} state. "" — a cleared native field — keeps the current
// period with empty hours/minutes, which the calculations read as "no time".
const to12h = (time24, emptyPeriod = "AM") => {
  if (!time24) return { hours: "", minutes: "", period: emptyPeriod };
  const [h, m] = time24.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hours12 = h % 12 || 12;
  return {
    hours: String(hours12).padStart(2, "0"),
    minutes: String(m).padStart(2, "0"),
    period,
  };
};

// The form's 12h state → a native <input type="time"> value ("" when incomplete).
const toTimeInputValue = ({ hours, minutes, period }) =>
  hours && minutes ? to24Hour(`${hours}:${minutes}`, period) : "";

const AddTimeRecordModal = ({
  isOpen,
  onClose,
  selectedDate,
  companyId,
  onSave,
  onDelete, // Callback for delete action
  preSelectedEmployee,
  source = "time-tracking", // "time-tracking" or "employee-dtr"
  mode = "add", // "add" or "edit"
  recordToEdit = null, // Record data for editing
  payrollRates = null, // Payroll rates (financial rates only)
  payrollRatesError = null, // Error message if rates not configured
  attendancePolicy = null, // Attendance policy for ND, defaults, leeway
  userDocId = null, // Stable user doc ID for audit trail
  lockStatus, // Derived DTR lock status for recordToEdit (C1/C6); NO default — an
              // omitted value in edit mode fails CLOSED (treated as locked), F12.
  lockRunId = null, // Derived covering-run id (for the soft-lock banner text)
}) => {
  // Hardcoded fallback when no employee is selected yet
  const defaultTimeIn = { hours: "08", minutes: "00", period: "AM" };
  const defaultTimeOut = { hours: "05", minutes: "00", period: "PM" };
  const defaultBreak = 60;

  // Form state - initialize with preSelectedEmployee if provided
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [timeInDate, setTimeInDate] = useState(""); // Only for employee-dtr mode
  const [timeInValue, setTimeInValue] = useState(defaultTimeIn);
  const [timeOutDate, setTimeOutDate] = useState("");
  const [timeOutValue, setTimeOutValue] = useState(defaultTimeOut);
  const [breakMinutes, setBreakMinutes] = useState(defaultBreak);
  const [notes, setNotes] = useState("");

  // Leave state
  const [onLeave, setOnLeave] = useState(false);
  const [isPaidLeave, setIsPaidLeave] = useState(true);

  // Absent state
  const [isAbsent, setIsAbsent] = useState(false);

  // Piece rate state
  const [isPieceRate, setIsPieceRate] = useState(false);
  const [pieceRateValue, setPieceRateValue] = useState('');

  // UI state
  const [employees, setEmployees] = useState([]);
  const [holiday, setHoliday] = useState(null);
  const [calculations, setCalculations] = useState({
    overtime: 0,
    undertime: 0,
    nightDiff: 0,
    hoursWorked: 0,
    late: 0,
    scheduleApplied: null,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const titleId = useId(); // the header's <h2>, for the shell's aria-labelledby
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // Attendance policy fetched fresh each time the modal opens (see effect below).
  // The `attendancePolicy` prop is only a fallback for the brief window before the
  // fetch resolves — the parent caches it at page mount and never refreshes, so a
  // mid-session Settings change would otherwise be stale here.
  const [freshPolicy, setFreshPolicy] = useState(null);

  // Delete confirmation modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Add-mode lock (PRD_TimeRecordAndPayFixes C5b): the modal reads the payroll index
  // itself, so every add entry point — empty row, page Add buttons, the in-modal date
  // picker, TimeTracking — is guarded the same way. Save waits for the read: with no
  // runs loaded deriveLockStatus returns null, which would let a paid date through.
  const [addLockRuns, setAddLockRuns] = useState([]);
  const [loadingLocks, setLoadingLocks] = useState(false);
  const [addLockReadFailed, setAddLockReadFailed] = useState(false);

  // Determine if the current date is a rest day for the selected employee
  const isRestDay = useMemo(() => {
    if (!selectedEmployee) return false;
    const dateForSchedule = source === "employee-dtr"
      ? (timeInDate ? new Date(timeInDate + "T00:00:00") : null)
      : selectedDate;
    if (!dateForSchedule) return false;
    return isRestDayForEmployee(selectedEmployee, dateForSchedule);
  }, [selectedEmployee, source, timeInDate, selectedDate]);

  // Hard lock detection — completed AND pending-approval payroll locks block all editing
  // (PRD_PayrollApproval §DTR Lock Behavior — pending-approval is hard-lock-equivalent)
  const isHardLocked = mode === "edit"
    && (lockStatus === undefined || isHardLock(lockStatus)); // undefined = caller omitted → fail closed (F12)

  // C5b/c: the add-mode lock for the chosen employee and the record's start date
  // (keyed on the 4-digit employeeId the index stores, same as the kiosk).
  const addLockDateYMD = source === "employee-dtr"
    ? timeInDate
    : (selectedDate ? formatDateString(selectedDate) : "");
  const addLock = mode === "add" && !addLockReadFailed && selectedEmployee
    ? deriveLockStatus(selectedEmployee.employeeId, addLockDateYMD, addLockRuns)
    : null;
  const isAddBlocked = mode === "add"
    && (addLockReadFailed || (!!addLock && isHardLock(addLock.status)));
  const isAddWaitingOnLocks = mode === "add" && loadingLocks;

  // Effective attendance policy: prefer the freshly-fetched copy, fall back to the
  // (possibly stale) prop until the fetch resolves. Drives both the on-screen OT/ND
  // calculation and the saved otApproval/policyApplied snapshot so they stay consistent.
  const effectivePolicy = freshPolicy || attendancePolicy;

  // Load active employees and set preSelectedEmployee when modal opens
  useEffect(() => {
    if (isOpen && companyId && selectedDate) {
      loadEmployees();
      checkForHoliday();

      // Initialize dates based on source mode
      if (source === "employee-dtr") {
        // For employee-dtr mode, initialize timeInDate to selectedDate
        const initialDate = formatDateString(selectedDate);
        setTimeInDate(initialDate);
        setTimeOutDate(initialDate);
      } else {
        // For time-tracking mode, use selectedDate for timeOut
        setTimeOutDate(formatDateString(selectedDate));
      }

      // Set preSelectedEmployee if provided
      if (preSelectedEmployee) {
        setSelectedEmployee(preSelectedEmployee);
      }
    }
  }, [isOpen, companyId, selectedDate, preSelectedEmployee, source]);

  // Refresh the attendance policy each time the modal opens, so a threshold/leeway/ND
  // change made in Settings is reflected without a full page reload (the parent only
  // fetches it once at mount). getAttendancePolicy merges DEFAULT_ATTENDANCE_POLICY.
  useEffect(() => {
    if (!isOpen || !companyId) return;
    let cancelled = false;
    getAttendancePolicy(companyId)
      .then((p) => { if (!cancelled) setFreshPolicy(p); })
      .catch((err) => { console.error("[AddTimeRecordModal] policy refresh failed:", err); });
    return () => { cancelled = true; };
  }, [isOpen, companyId]);

  // Add mode only: one payroll-index read per open (C5b). A throw fails closed.
  useEffect(() => {
    if (!isOpen || mode !== "add" || !companyId) return;
    let cancelled = false;
    setLoadingLocks(true);
    setAddLockReadFailed(false);
    loadActiveLocks(companyId)
      .then((runs) => { if (!cancelled) setAddLockRuns(runs || []); })
      .catch((err) => {
        console.error("[AddTimeRecordModal] lock index read failed — fail-closed:", err);
        if (!cancelled) setAddLockReadFailed(true);
      })
      .finally(() => { if (!cancelled) setLoadingLocks(false); });
    return () => { cancelled = true; };
  }, [isOpen, mode, companyId]);

  // Pre-populate form when editing
  useEffect(() => {
    if (isOpen && mode === "edit" && recordToEdit) {
      // Set employee
      if (preSelectedEmployee) {
        setSelectedEmployee(preSelectedEmployee);
      }

      // Check if this is a leave record
      const isLeaveRecord = recordToEdit.onLeave === "yes";
      setOnLeave(isLeaveRecord);
      setIsPaidLeave(recordToEdit.isPaidLeave !== undefined ? recordToEdit.isPaidLeave : true);

      // Check if this is an absent record
      const isAbsentRecord = recordToEdit.isAbsent === true;
      setIsAbsent(isAbsentRecord);

      // Check if this is a piece rate record
      setIsPieceRate(recordToEdit.isPieceRate || false);
      setPieceRateValue(recordToEdit.pieceRateValue ? recordToEdit.pieceRateValue.toString() : '');

      // Set dates
      setTimeInDate(recordToEdit.date);
      setTimeOutDate(recordToEdit.date); // Default to same day

      if (isLeaveRecord || isAbsentRecord) {
        // For leave/absent records, don't populate time fields (keep them empty/default)
        setTimeInValue({ hours: "", minutes: "", period: "AM" });
        setTimeOutValue({ hours: "", minutes: "", period: "PM" });
        setBreakMinutes(0);
      } else {
        // Parse the stored instants as MANILA wall time (C3) — a browser in another
        // zone would otherwise show, and on re-save write back, a shifted time.
        if (recordToEdit.timeIn && recordToEdit.timeIn.toDate) {
          setTimeInValue(to12h(manilaHHMM(recordToEdit.timeIn.toDate())));
        }

        if (recordToEdit.timeOut && recordToEdit.timeOut.toDate) {
          const timeOutDateObj = recordToEdit.timeOut.toDate();
          setTimeOutValue(to12h(manilaHHMM(timeOutDateObj)));

          // Set the actual time out date (may be next day for overnight shifts)
          setTimeOutDate(manilaYMD(timeOutDateObj));
        }

        // Set break minutes for normal records
        setBreakMinutes(recordToEdit.breakMinutes ?? 60);
      }

      // Set notes (available for both leave and normal records)
      setNotes(recordToEdit.notes || "");
    }
  }, [isOpen, mode, recordToEdit, preSelectedEmployee]);

  // Pre-fill time in/out from employee's work schedule (add mode only)
  // No Firestore read — uses the already-loaded selectedEmployee object in memory
  useEffect(() => {
    if (!isOpen || mode !== "add" || !selectedEmployee) return;

    // Determine the date to look up the schedule for
    const dateForSchedule = source === "employee-dtr"
      ? (timeInDate ? new Date(timeInDate + "T00:00:00") : null)
      : selectedDate;
    if (!dateForSchedule) return;

    const dayName = getDayName(dateForSchedule);
    const schedule = selectedEmployee?.employment?.workSchedule?.[dayName];

    // Only pre-fill from working day schedules with valid times
    if (!schedule?.isWorkingDay || !schedule?.startTime || !schedule?.endTime) return;

    setTimeInValue(to12h(schedule.startTime));
    setTimeOutValue(to12h(schedule.endTime));
    setBreakMinutes(schedule.breakDuration);
  }, [isOpen, mode, selectedEmployee, source, timeInDate, selectedDate]);

  const loadEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const activeEmployees = await getActiveEmployees(companyId, selectedDate);
      setEmployees(activeEmployees);
    } catch (error) {
      console.error("Error loading employees:", error);
      setEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const checkForHoliday = async () => {
    try {
      const holidayData = await checkHoliday(companyId, selectedDate);
      setHoliday(holidayData);
    } catch (error) {
      console.error("Error checking holiday:", error);
    }
  };

  // Calculate hours whenever inputs change
  useEffect(() => {
    // Skip calculations if on leave or absent
    if (onLeave || isAbsent) {
      setCalculations({
        hoursWorked: 0,
        overtime: 0,
        undertime: 0,
        nightDiff: 0,
        late: 0,
        scheduleApplied: null,
      });
      return;
    }

    // If piece rate is active, zero out overtime and night diff
    if (isPieceRate) {
      // Still calculate hours worked for attendance tracking
      const effectiveTimeInDate =
        source === "employee-dtr" ? timeInDate : formatDateString(selectedDate);

      if (
        effectiveTimeInDate &&
        timeInValue.hours &&
        timeInValue.minutes &&
        timeOutValue.hours &&
        timeOutValue.minutes
      ) {
        const timeIn = createDateTime(
          effectiveTimeInDate,
          `${timeInValue.hours}:${timeInValue.minutes}`,
          timeInValue.period
        );
        const timeOut = createDateTime(
          timeOutDate,
          `${timeOutValue.hours}:${timeOutValue.minutes}`,
          timeOutValue.period
        );

        const hoursWorked = calculateWorkingHours(timeIn, timeOut, breakMinutes);

        setCalculations({
          hoursWorked,
          overtime: 0, // Force to 0 for piece rate
          undertime: 0, // Force to 0 for piece rate
          nightDiff: 0, // Force to 0 for piece rate
          late: 0,
          scheduleApplied: null,
        });
      } else {
        setCalculations({
          hoursWorked: 0,
          overtime: 0,
          undertime: 0,
          nightDiff: 0,
          late: 0,
          scheduleApplied: null,
        });
      }
      return;
    }

    // Determine which date to use for Time In based on source
    const effectiveTimeInDate =
      source === "employee-dtr" ? timeInDate : formatDateString(selectedDate);

    if (effectiveTimeInDate && timeInValue.hours && timeInValue.minutes) {
      const timeIn = createDateTime(
        effectiveTimeInDate,
        `${timeInValue.hours}:${timeInValue.minutes}`,
        timeInValue.period
      );

      // Resolve employee schedule for the selected date
      const dateForSchedule = source === "employee-dtr"
        ? new Date(timeInDate + "T00:00:00")
        : selectedDate;
      const dayName = getDayName(dateForSchedule);
      const schedule = selectedEmployee?.employment?.workSchedule?.[dayName];
      const isWorkingDay = schedule?.isWorkingDay === true;
      const hasSchedule = isWorkingDay && schedule?.startTime && schedule?.endTime;

      // Apply early/late leeway for working days with schedule (time-in side)
      let effectiveTimeIn = timeIn;
      const scheduledStartDate = hasSchedule
        ? buildScheduleDateTime(effectiveTimeInDate, schedule.startTime)
        : null;
      if (hasSchedule && effectivePolicy) {
        effectiveTimeIn = calculateEffectiveTimeIn(
          timeIn, scheduledStartDate, effectivePolicy.leewayEarlyTimeIn, effectivePolicy.leewayLateGracePeriod
        );
      }

      // Late is anchored to time-in ONLY — computed even for an open shift (no time-out yet).
      // Grace is already absorbed by effectiveTimeIn (0 within grace, full lateness past it).
      let lateHours = 0;
      if (hasSchedule && effectivePolicy && scheduledStartDate) {
        lateHours = calculateLateHours(effectiveTimeIn, scheduledStartDate);
      }

      // Schedule snapshot — schedule-sourced fields only (audits isRestDay + late).
      const scheduleApplied = hasSchedule
        ? {
            isWorkingDay: true,
            startTime: schedule.startTime,
            endTime: schedule.endTime,
            breakDuration: schedule.breakDuration ?? null,
          }
        : null;

      // Hours-dependent values (hoursWorked / OT / UT / ND) require a time-out.
      let hoursWorked = 0;
      let overtimeHours = 0;
      let undertimeHours = 0;
      let nightDiffHours = 0;
      if (timeOutValue.hours && timeOutValue.minutes) {
        const timeOut = createDateTime(
          timeOutDate,
          `${timeOutValue.hours}:${timeOutValue.minutes}`,
          timeOutValue.period
        );

        // Apply OT-start / undertime-grace leeway (time-out side)
        let effectiveTimeOut = timeOut;
        if (hasSchedule && effectivePolicy) {
          const scheduledEnd = buildScheduleDateTime(effectiveTimeInDate, schedule.endTime);
          // Cross-day shift: roll scheduledEnd to next day if needed
          if (scheduledEnd <= scheduledStartDate) {
            // +24h on the instant, not setDate: a local-day roll moves the scheduled
            // end by an hour on a daylight-saving night in the browser's zone (C3).
            scheduledEnd.setTime(scheduledEnd.getTime() + 24 * 60 * 60 * 1000);
          }
          effectiveTimeOut = calculateEffectiveTimeOut(
            timeOut, scheduledEnd, effectivePolicy.leewayOtStart, effectivePolicy.leewayUndertimeGracePeriod
          );
        }

        hoursWorked = calculateWorkingHours(effectiveTimeIn, effectiveTimeOut, breakMinutes);

        // OT and undertime — hours-based for all day types (CL-002). Leeway clamping
        // already folded early-in / minor-late / minor-early-out into the effective times.
        // hoursPerDay lives on attendancePolicy (C4); payrollRates is the pre-backfill
        // fallback window — remove the fallback once the migration has run everywhere.
        // Guard: this preview effect can run at mount BEFORE the settings props
        // finish loading — skip the OT/UT preview until the value resolves (the
        // effect re-runs when the policy arrives); throwing here crashed the
        // whole Time Tracking page. Save-side validation still enforces config.
        const hoursPerDaySetting = effectivePolicy?.hoursPerDay ?? payrollRates?.hoursPerDay;
        if (hoursPerDaySetting > 0) { // >0, not just typeof number: also skips an invalid stored 0 (no /0 → Infinity)
          const rawOvertimeHours = calculateOvertimeRestDay(hoursWorked, hoursPerDaySetting);
          overtimeHours = applyOtIncrement(rawOvertimeHours, effectivePolicy?.otIncrement || 0);
          undertimeHours = calculateUndertime(hoursWorked, hoursPerDaySetting);
        }

        // Night diff using effectiveTimeIn (leeway-clamped) and the raw time-out
        if (effectivePolicy && typeof effectivePolicy.nightDiffStart === 'number' && typeof effectivePolicy.nightDiffEnd === 'number') {
          nightDiffHours = calculateNightDiff(effectiveTimeIn, timeOut, {
            breakMinutes,
            nightDiffStart: effectivePolicy.nightDiffStart,
            nightDiffEnd: effectivePolicy.nightDiffEnd,
          });
        }
      }

      setCalculations({
        hoursWorked,
        overtime: overtimeHours,
        undertime: undertimeHours,
        nightDiff: nightDiffHours,
        late: lateHours,
        scheduleApplied,
      });
    } else {
      // C2: a cleared Time In (the native field reports "") shows no figures, rather
      // than leaving the last computed ones on screen.
      setCalculations({
        hoursWorked: 0, overtime: 0, undertime: 0, nightDiff: 0, late: 0, scheduleApplied: null,
      });
    }
  }, [
    timeInValue,
    timeOutDate,
    timeOutValue,
    breakMinutes,
    selectedDate,
    source,
    timeInDate,
    onLeave,
    isAbsent,
    isPieceRate,
    effectivePolicy,
    payrollRates,
    selectedEmployee,
  ]);

  // Check if ND break ambiguity warning should be shown
  const showNDBreakWarning = useMemo(() => {
    if (onLeave || isAbsent || isPieceRate) return false;
    if (!effectivePolicy || typeof effectivePolicy.nightDiffStart !== 'number') return false;

    const effectiveTimeInDate =
      source === "employee-dtr" ? timeInDate : formatDateString(selectedDate);

    if (!effectiveTimeInDate || !timeOutDate) return false;
    // C2: a cleared field would otherwise read as midnight/noon in to24Hour and
    // flash a false warning while the user retypes.
    if (!timeInValue.hours || !timeInValue.minutes || !timeOutValue.hours || !timeOutValue.minutes) {
      return false;
    }

    const timeIn = createDateTime(
      effectiveTimeInDate,
      `${timeInValue.hours}:${timeInValue.minutes}`,
      timeInValue.period
    );
    const timeOut = createDateTime(
      timeOutDate,
      `${timeOutValue.hours}:${timeOutValue.minutes}`,
      timeOutValue.period
    );

    return hasNDBreakAmbiguity(timeIn, timeOut, {
      breakMinutes,
      nightDiffStart: effectivePolicy.nightDiffStart,
      nightDiffEnd: effectivePolicy.nightDiffEnd,
    });
  }, [
    timeInValue,
    timeOutDate,
    timeOutValue,
    breakMinutes,
    selectedDate,
    source,
    timeInDate,
    onLeave,
    isAbsent,
    isPieceRate,
    effectivePolicy,
  ]);

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    setErrors((prev) => ({ ...prev, employee: "" }));
  };

  const handleEmployeeRemove = () => {
    setSelectedEmployee(null);
  };

  const handleTimeInDateChange = (e) => {
    const newDate = e.target.value;
    setTimeInDate(newDate);

    // Auto-update time out date to same day
    setTimeOutDate(newDate);
    setErrors((prev) => ({ ...prev, timeIn: "" }));
  };

  const handleTimeOutDateChange = (e) => {
    const newDate = e.target.value;

    if (source === "employee-dtr") {
      // For employee-dtr mode, validate against timeInDate
      if (!timeInDate) return;

      const timeInDateObj = new Date(timeInDate + "T00:00:00");
      const nextDay = new Date(timeInDateObj);
      nextDay.setDate(nextDay.getDate() + 1);
      const nextDayStr = formatDateString(nextDay);

      if (newDate === timeInDate || newDate === nextDayStr) {
        setTimeOutDate(newDate);
        setErrors((prev) => ({ ...prev, timeOut: "" }));
      } else {
        setErrors((prev) => ({
          ...prev,
          timeOut: "Time out date cannot be more than 1 day after time in",
        }));
      }
    } else {
      // For time-tracking mode, validate against selectedDate
      const selectedDateStr = selectedDate
        ? formatDateString(selectedDate)
        : "";
      if (!selectedDate) return;

      const nextDay = new Date(selectedDate);
      nextDay.setDate(nextDay.getDate() + 1);
      const nextDayStr = formatDateString(nextDay);

      if (newDate === selectedDateStr || newDate === nextDayStr) {
        setTimeOutDate(newDate);
        setErrors((prev) => ({ ...prev, timeOut: "" }));
      } else {
        setErrors((prev) => ({
          ...prev,
          timeOut: "Time out date cannot be more than 1 day after time in",
        }));
      }
    }
  };

  const handleNotesChange = (e) => {
    const value = e.target.value;
    if (value.length <= 100) {
      setNotes(value);
    }
  };

  const handlePieceRateChange = (e) => {
    const checked = e.target.checked;
    setIsPieceRate(checked);

    // Clear piece rate value if unchecking
    if (!checked) {
      setPieceRateValue('');
      setErrors((prev) => ({ ...prev, pieceRate: '' }));
    }

    // If checking piece rate, uncheck leave
    if (checked && onLeave) {
      setOnLeave(false);
      setIsPaidLeave(true);
    }
  };

  const handlePieceRateValueChange = (e) => {
    const value = e.target.value;

    // Allow only numbers and decimal point
    const sanitized = value.replace(/[^\d.]/g, '');

    // Prevent multiple decimal points
    const parts = sanitized.split('.');
    const formatted = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : sanitized;

    setPieceRateValue(formatted);

    // Validate
    if (formatted) {
      const numValue = parseFloat(formatted);
      if (numValue < 100) {
        setErrors((prev) => ({ ...prev, pieceRate: 'Minimum amount is ₱100' }));
      } else if (numValue > 10000) {
        setErrors((prev) => ({ ...prev, pieceRate: 'Maximum amount is ₱10,000' }));
      } else {
        setErrors((prev) => ({ ...prev, pieceRate: '' }));
      }
    } else {
      setErrors((prev) => ({ ...prev, pieceRate: '' }));
    }
  };

  // Handle attendance status change (Worked, Leave, Absent)
  const handleAttendanceStatusChange = (status) => {
    if (status === 'worked') {
      setOnLeave(false);
      setIsAbsent(false);
    } else if (status === 'leave') {
      setOnLeave(true);
      setIsAbsent(false);
      // Clear piece rate when switching to leave
      if (isPieceRate) {
        setIsPieceRate(false);
        setPieceRateValue('');
        setErrors((prev) => ({ ...prev, pieceRate: '' }));
      }
    } else if (status === 'absent') {
      setOnLeave(false);
      setIsAbsent(true);
      // Clear piece rate when switching to absent
      if (isPieceRate) {
        setIsPieceRate(false);
        setPieceRateValue('');
        setErrors((prev) => ({ ...prev, pieceRate: '' }));
      }
    }
  };

  // Derive current attendance status for UI
  const getAttendanceStatus = () => {
    if (isAbsent) return 'absent';
    if (onLeave) return 'leave';
    return 'worked';
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedEmployee) {
      newErrors.employee = "Please select an employee";
    }

    // BL-043 — the CHOSEN Time In date must fall inside the employment window.
    // Only the date the modal was OPENED on is filtered (the `employees` list at
    // ~:1094 comes from getActiveEmployees(companyId, selectedDate), and that
    // effect doesn't depend on timeInDate), so opening on an in-window day and
    // then changing the date saved out-of-employment attendance into payroll.
    //
    // Deliberately placed ABOVE the onLeave/isAbsent early return below: leave
    // and absent records are exactly what payroll pays and deducts, so a guard
    // sitting with the other date checks would skip them.
    //
    // Add mode only (C4): the date input is readOnly+disabled when mode==="edit"
    // (~:1640), so an edit cannot move the date out of range — and blocking there
    // would strand a record that fell outside the window after HR back-dated an
    // endDate. Skipped on a blank date; "Please select a time in date" owns that.
    if (
      source === "employee-dtr" &&
      mode !== "edit" &&
      selectedEmployee &&
      timeInDate &&
      !isEmployeeActiveOnDate(selectedEmployee, timeInDate)
    ) {
      const name =
        selectedEmployee.personalInfo?.fullName ||
        `${selectedEmployee.personalInfo?.firstName || ""} ${selectedEmployee.personalInfo?.lastName || ""}`.trim() ||
        "This employee";
      const start = formatYMDForDisplay(selectedEmployee.employment?.startDate);
      const end = formatYMDForDisplay(selectedEmployee.employment?.endDate);
      const range = end ? `${start} – ${end}` : `from ${start}`;
      // `save`, not `timeIn` — this is the modal's general banner in the sticky
      // footer, where every other blocking save message already goes. It is about
      // the record as a whole, not the Time In field, and the footer is always
      // visible where a field-level error can sit off-screen.
      newErrors.save = `${name} was not employed on this date (employed ${range}).`;
    }

    // Validate piece rate if checked
    if (isPieceRate) {
      if (!pieceRateValue) {
        newErrors.pieceRate = "Please enter piece rate amount";
      } else {
        const numValue = parseFloat(pieceRateValue);
        if (numValue < 100) {
          newErrors.pieceRate = "Minimum amount is ₱100";
        } else if (numValue > 10000) {
          newErrors.pieceRate = "Maximum amount is ₱10,000";
        }
      }
    }

    // Skip time validation if on leave or absent
    if (onLeave || isAbsent) {
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }

    // Validate Time In date for employee-dtr mode
    if (source === "employee-dtr" && !timeInDate) {
      newErrors.timeIn = "Please select a time in date";
    }

    if (!timeInValue.hours || !timeInValue.minutes) {
      newErrors.timeIn = "Please enter time in";
    }

    // Determine effective date for validation
    const effectiveTimeInDate =
      source === "employee-dtr" ? timeInDate : formatDateString(selectedDate);

    if (effectiveTimeInDate && timeOutValue.hours && timeOutValue.minutes) {
      const timeIn = createDateTime(
        effectiveTimeInDate,
        `${timeInValue.hours}:${timeInValue.minutes}`,
        timeInValue.period
      );
      const timeOut = createDateTime(
        timeOutDate,
        `${timeOutValue.hours}:${timeOutValue.minutes}`,
        timeOutValue.period
      );

      if (timeOut <= timeIn) {
        newErrors.timeOut = "Time out must be after time in";
      } else if (timeOut - timeIn > 24 * 60 * 60 * 1000) {
        // C11: the next-day time-out date makes a 24h+ shift a one-click mistake.
        // Same rule and message as validateTimeRecord, written here so the modal's
        // own test (which mocks the service) can exercise it.
        newErrors.timeOut = "Time out must be within 24 hours of time in";
      }

      if (breakMinutes > calculations.hoursWorked * 60) {
        newErrors.break = "Break cannot exceed working hours";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      // Determine effective Time In date based on source
      const effectiveTimeInDate =
        source === "employee-dtr" ? timeInDate : formatDateString(selectedDate);

      if (!effectiveTimeInDate) {
        setErrors({ save: "No date selected. Please try again." });
        setSaving(false);
        return;
      }

      let timeIn, timeOut;

      if (onLeave || isAbsent) {
        // For leave/absent records, use midnight of that date
        timeIn = new Date(effectiveTimeInDate + 'T00:00:00+08:00'); // Manila midnight (C3)
        timeOut = null;
      } else {
        // For normal records, use actual time values
        timeIn = createDateTime(
          effectiveTimeInDate,
          `${timeInValue.hours}:${timeInValue.minutes}`,
          timeInValue.period
        );

        timeOut = null;
        if (timeOutValue.hours && timeOutValue.minutes) {
          timeOut = createDateTime(
            timeOutDate,
            `${timeOutValue.hours}:${timeOutValue.minutes}`,
            timeOutValue.period
          );
        }
      }

      if (mode === "edit" && recordToEdit) {
        // Update existing record
        // Determine timeInSource based on attendance status
        let timeInSource = recordToEdit.timeInSource;
        if (onLeave) {
          timeInSource = 'leave';
        } else if (isAbsent) {
          timeInSource = 'absent';
        }

        const updates = {
          timeIn: timeIn,
          timeOut: timeOut,
          breakMinutes: onLeave || isAbsent ? 0 : (breakMinutes || 0),
          notes,
          hoursWorked: calculations.hoursWorked,
          regularHours: Math.min(8, calculations.hoursWorked),
          overtimeHours: calculations.overtime,
          undertimeHours: calculations.undertime,
          nightDiffHours: calculations.nightDiff,
          lateHours: calculations.late,
          isLate: calculations.late > 0,
          timeInSource: timeInSource,
          timeOutSource: onLeave || isAbsent ? null : recordToEdit.timeOutSource,
          onLeave: onLeave ? "yes" : "no",
          isPaidLeave: isPaidLeave,
          isAbsent: isAbsent,
          // Piece rate fields
          isPieceRate: isPieceRate,
          pieceRateValue: isPieceRate ? Math.round(parseFloat(pieceRateValue) * 100) / 100 : null,
          // Schedule snapshot
          isRestDay: isRestDay,
          scheduleApplied: calculations.scheduleApplied,
          // Leeway policy snapshot
          policyApplied: effectivePolicy ? {
            leewayEarlyTimeIn: effectivePolicy.leewayEarlyTimeIn,
            leewayLateGracePeriod: effectivePolicy.leewayLateGracePeriod,
            leewayOtStart: effectivePolicy.leewayOtStart,
            leewayUndertimeGracePeriod: effectivePolicy.leewayUndertimeGracePeriod,
            otIncrement: effectivePolicy.otIncrement,
            nightDiffStart: effectivePolicy.nightDiffStart,
            nightDiffEnd: effectivePolicy.nightDiffEnd,
          } : null,
          // Audit trail
          updatedBy: userDocId,
          // OT Approval
          otApproval: (() => {
            const otHours = calculations.overtime;
            const threshold = effectivePolicy?.otAutoApprovalThreshold ?? 0;
            const newStatus = evaluateOtStatus(otHours, threshold);
            if (!newStatus) return null;
            const now = new Date();
            const newAction = newStatus === "auto-approved" ? "auto-approved" : "submitted";
            const existing = recordToEdit?.otApproval;
            const trail = existing
              ? [
                  ...(existing.auditTrail || []),
                  { action: "reset", by: userDocId, at: now, reason: null, otHours },
                  { action: newAction, by: userDocId, at: now, reason: null, otHours },
                ]
              : [{ action: newAction, by: userDocId, at: now, reason: null, otHours }];
            return { status: newStatus, otAutoApprovalThreshold: threshold, auditTrail: trail };
          })(),
        };

        await updateTimeRecord(
          companyId,
          selectedEmployee.employeeId,
          recordToEdit.id,
          updates
        );

        // Update OT approval summary counters
        const oldStatus = recordToEdit?.otApproval?.status || null;
        const newStatus = updates.otApproval?.status || null;
        if (oldStatus !== newStatus) {
          updateOtApprovalSummary(companyId, oldStatus, newStatus).catch(console.error);
        }

        // For edit mode, just call onSave without record data
        if (onSave) {
          await onSave();
        }
      } else {
        // Create new record

        // Guard a stale selection (e.g. the employee was deactivated in another
        // session) against the already-loaded active-employee list — no Firestore
        // read (the old contribution fetch that incidentally caught this is gone).
        if (!employees.some((e) => e.id === selectedEmployee.id)) {
          throw new Error('Selected employee is no longer available. Please reselect.');
        }

        const recordData = {
          employeeId: selectedEmployee.employeeId,
          employeeDocId: selectedEmployee.id,
          timeIn,
          timeOut,
          breakMinutes: onLeave || isAbsent ? 0 : (breakMinutes || 0),
          notes,
          hoursWorked: calculations.hoursWorked,
          regularHours: Math.min(8, calculations.hoursWorked),
          overtimeHours: calculations.overtime,
          undertimeHours: calculations.undertime,
          nightDiffHours: calculations.nightDiff,
          lateHours: calculations.late,
          isLate: calculations.late > 0,
          onLeave: onLeave ? "yes" : "no",
          isPaidLeave: isPaidLeave,
          isAbsent: isAbsent,
          // Piece rate fields
          isPieceRate: isPieceRate,
          pieceRateValue: isPieceRate ? Math.round(parseFloat(pieceRateValue) * 100) / 100 : null,
          // Schedule snapshot
          isRestDay: isRestDay,
          scheduleApplied: calculations.scheduleApplied,
          // Leeway policy snapshot
          policyApplied: effectivePolicy ? {
            leewayEarlyTimeIn: effectivePolicy.leewayEarlyTimeIn,
            leewayLateGracePeriod: effectivePolicy.leewayLateGracePeriod,
            leewayOtStart: effectivePolicy.leewayOtStart,
            leewayUndertimeGracePeriod: effectivePolicy.leewayUndertimeGracePeriod,
            otIncrement: effectivePolicy.otIncrement,
            nightDiffStart: effectivePolicy.nightDiffStart,
            nightDiffEnd: effectivePolicy.nightDiffEnd,
          } : null,
          // Audit trail
          createdBy: userDocId,
          // OT Approval
          otApproval: (() => {
            const otHours = calculations.overtime;
            const threshold = effectivePolicy?.otAutoApprovalThreshold ?? 0;
            const newStatus = evaluateOtStatus(otHours, threshold);
            if (!newStatus) return null;
            const now = new Date();
            const newAction = newStatus === "auto-approved" ? "auto-approved" : "submitted";
            return {
              status: newStatus,
              otAutoApprovalThreshold: threshold,
              auditTrail: [{ action: newAction, by: userDocId, at: now, reason: null, otHours }],
            };
          })(),
        };

        const savedRecord = await saveTimeRecord(companyId, recordData);

        // Update OT approval summary counters
        const newStatus = recordData.otApproval?.status || null;
        if (newStatus) {
          updateOtApprovalSummary(companyId, null, newStatus).catch(console.error);
        }

        // Call the onSave callback with the saved record for optimistic updates
        if (onSave) {
          await onSave(savedRecord);
        }
      }

      // Reset form and close modal
      resetForm();
      onClose();
    } catch (error) {
      console.error(
        `Error ${mode === "edit" ? "updating" : "saving"} time record:`,
        error
      );
      setErrors({
        save:
          error.message ||
          `Failed to ${
            mode === "edit" ? "update" : "save"
          } time record. Please try again.`,
      });
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setSelectedEmployee(null);
    setTimeInDate("");
    setTimeInValue(defaultTimeIn);
    setTimeOutDate(selectedDate ? formatDateString(selectedDate) : "");
    setTimeOutValue(defaultTimeOut);
    setBreakMinutes(defaultBreak);
    setNotes("");
    setOnLeave(false);
    setIsPaidLeave(true);
    setIsAbsent(false);
    setIsPieceRate(false);
    setPieceRateValue('');
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleDelete = () => {
    if (!recordToEdit || !onDelete) return;
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    onDelete(recordToEdit.id);
  };

  // Calculate month boundaries for employee-dtr mode
  // Based on today's date - allows current month and previous month only
  const monthBoundaries = useMemo(() => {
    if (source !== "employee-dtr") return null;

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    // First day of previous month
    const firstDay = new Date(currentYear, currentMonth - 1, 1);
    // Last day of current month
    const lastDay = new Date(currentYear, currentMonth + 1, 0);

    return {
      min: formatDateString(firstDay),
      max: formatDateString(lastDay),
    };
  }, [source]);

  // Calculate next day based on source mode
  const nextDay = useMemo(() => {
    if (source === "employee-dtr") {
      // For employee-dtr mode, calculate from timeInDate
      if (!timeInDate) return "";
      const timeInDateObj = new Date(timeInDate + "T00:00:00");
      const tomorrow = new Date(timeInDateObj);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return formatDateString(tomorrow);
    } else {
      // For time-tracking mode, calculate from selectedDate
      if (!selectedDate) return "";
      const tomorrow = new Date(selectedDate);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return formatDateString(tomorrow);
    }
  }, [source, timeInDate, selectedDate]);

  if (!isOpen) return null;

  return (
    // The shell centres the card with flex — no transform here, or the
    // stacked Delete confirm's fixed layer would shrink to this card.
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      busy={saving}
      labelledBy={titleId}
      className="app-modal__dialog--dark-header"
      style={{
        width: "540px",
        maxWidth: "calc(100vw - 48px)",
        maxHeight: "90vh",
        background: "var(--ui-surface)",
        borderRadius: "12px",
        boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
        <div
          style={{
            background: isHardLocked
              ? "linear-gradient(135deg, var(--ui-text-muted) 0%, var(--ui-text-secondary) 100%)"
              : isRestDay
              ? "linear-gradient(135deg, var(--ui-accent) 0%, var(--ui-warning-hover) 100%)"
              : "var(--ui-gradient-primary)",
            color: "white",
            padding: "20px 24px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            flexShrink: 0,
            borderRadius: "12px 12px 0 0",
            alignItems: "flex-start",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
            <h2
              id={titleId}
              style={{
                margin: 0,
                fontSize: "18px",
                fontWeight: 700,
                color: "white",
                textAlign: "left",
              }}
            >
              {isHardLocked ? "View Time Record" : mode === "edit" ? "Edit Time Record" : "Daily Time Record"}
            </h2>
            {isHardLocked && (
              <span
                style={{
                  marginLeft: "8px",
                  padding: "2px 8px",
                  background: "rgba(255, 255, 255, 0.2)",
                  borderRadius: "4px",
                  fontSize: "11px",
                  fontWeight: 600,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Icon name="lock" size={10} />
                Locked
              </span>
            )}
            {isRestDay && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.95)",
                  background: "rgba(255,255,255,0.2)",
                  padding: "4px 10px",
                  borderRadius: "12px",
                  marginLeft: "8px",
                }}
              >
                Rest Day
              </span>
            )}
          </div>
        </div>

        <div
          className={isHardLocked ? "hard-locked-form" : undefined}
          style={{
            flex: 1,
            padding: "20px 24px",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          {isHardLocked && (
            <style>{`
              .hard-locked-form input,
              .hard-locked-form select,
              .hard-locked-form textarea,
              .hard-locked-form button,
              .hard-locked-form [role="button"],
              .hard-locked-form [style*="cursor: pointer"],
              .hard-locked-form [style*="cursor:pointer"],
              .hard-locked-form .calc-stats {
                pointer-events: none !important;
                opacity: 0.55 !important;
                cursor: default !important;
              }
            `}</style>
          )}
          {/* Employee — the shared picker, first field in the body (PRD_SharedEmployeePicker C5). */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "14px",
              marginTop: 0,
            }}
          >
            <div
              style={{
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            >
              <Icon name="user" size={18} />
            </div>
            <div
              style={{
                fontSize: "13px",
                fontWeight: 600,
                color: "var(--ui-text)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Employee
            </div>
          </div>
          <EmployeePicker
            employees={employees}
            value={selectedEmployee}
            onChange={(row) => (row ? handleEmployeeSelect(row) : handleEmployeeRemove())}
            locked={Boolean(selectedEmployee) && !(source === "time-tracking" && mode === "add")}
            emptyText={employees.length === 0 ? "No employees active on this date" : "No matching employees found"}
            disabled={loadingEmployees}
          />
          {errors.employee && (
            <span
              style={{
                color: "var(--ui-danger-text)",
                fontSize: "12px",
                margin: "-8px 0 12px",
                display: "block",
              }}
            >
              {errors.employee}
            </span>
          )}

          {/* Time In Section */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "14px",
              marginTop: 0,
            }}
          >
            <div
              style={{
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            >
              <Icon name="clock" size={18} />
            </div>
            <div
              style={{
                fontSize: "13px",
                fontWeight: 600,
                color: "var(--ui-text)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Time In
            </div>
            <div
              style={{
                flex: 1,
                height: "1px",
                background: "transparent",
              }}
            ></div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              <label
                style={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "var(--ui-text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Date
              </label>
              {source === "employee-dtr" ? (
                <input
                  type="date"
                  style={{
                    border: "1px solid var(--ui-border)",
                    borderRadius: "6px",
                    padding: "9px 12px",
                    fontSize: "14px",
                    color:
                      mode === "edit" || onLeave || isAbsent ? "var(--ui-text-muted)" : "var(--ui-text)",
                    background:
                      mode === "edit" || onLeave || isAbsent ? "var(--ui-surface-recessed)" : "white",
                    fontFamily: "var(--font-family-primary)",
                    cursor:
                      mode === "edit" || onLeave || isAbsent
                        ? "not-allowed"
                        : "pointer",
                    transition: "all 0.15s ease",
                  }}
                  value={timeInDate}
                  onChange={handleTimeInDateChange}
                  min={monthBoundaries?.min}
                  max={monthBoundaries?.max}
                  readOnly={mode === "edit" || onLeave || isAbsent}
                  disabled={mode === "edit" || onLeave || isAbsent}
                />
              ) : (
                <input
                  type="text"
                  style={{
                    border: "1px solid var(--ui-border)",
                    borderRadius: "6px",
                    padding: "9px 12px",
                    fontSize: "14px",
                    color: "var(--ui-text)",
                    background: "white",
                    fontFamily: "var(--font-family-primary)",
                    transition: "all 0.15s ease",
                  }}
                  value={selectedDate ? formatDisplayDate(selectedDate) : ""}
                  readOnly
                />
              )}
              {/* C5c: add-mode lock message under the date. */}
              {mode === "add" && (addLockReadFailed || addLock) && (
                <div
                  role={isAddBlocked ? "alert" : "status"}
                  style={{
                    fontSize: "12px",
                    lineHeight: 1.4,
                    color: isAddBlocked ? "var(--ui-danger-text)" : "var(--ui-warning-text)",
                  }}
                >
                  {addLockReadFailed
                    ? "Couldn't check payroll locks, so records can't be added right now. Reload the page to try again."
                    : addLock.status === "completed"
                    ? `Locked by Payroll Run ${addLock.runId} (Completed) — records can't be added to this date.`
                    : addLock.status === "pending-approval"
                    ? `Locked by pending payroll ${addLock.runId} — records can't be added to this date.`
                    : `This date is in Draft ${addLock.runId}. Adding a record will require the draft's gross income to be recalculated.`}
                </div>
              )}
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              <label
                style={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "var(--ui-text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Time
              </label>
              {/* C2: the native time field (as the Work Schedule page). Disabled only
                  for Leave / Absent — never for edit mode, the main correction flow.
                  No outline override, so the app-wide :focus-visible ring shows. */}
              <input
                type="time"
                aria-label="Time In"
                style={{
                  border: "1px solid var(--ui-border)",
                  borderRadius: "6px",
                  padding: "9px 12px",
                  fontSize: "14px",
                  fontVariantNumeric: "tabular-nums",
                  color: onLeave || isAbsent ? "var(--ui-text-muted)" : "var(--ui-text)",
                  background: onLeave || isAbsent ? "var(--ui-surface-recessed)" : "white",
                  fontFamily: "var(--font-family-primary)",
                  cursor: onLeave || isAbsent ? "not-allowed" : "text",
                }}
                value={toTimeInputValue(timeInValue)}
                onChange={(e) => {
                  const next = e.target.value;
                  setTimeInValue((prev) => to12h(next, prev.period));
                }}
                disabled={onLeave || isAbsent}
              />
              {errors.timeIn && (
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--ui-danger)",
                    marginTop: "0.25rem",
                  }}
                >
                  {errors.timeIn}
                </span>
              )}
            </div>
          </div>

          {/* Time Out Section */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "14px",
              marginTop: "16px",
            }}
          >
            <div
              style={{
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            >
              <Icon name="flag" size={18} />
            </div>
            <div
              style={{
                fontSize: "13px",
                fontWeight: 600,
                color: "var(--ui-text)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Time Out
            </div>
            <div
              style={{
                flex: 1,
                height: "1px",
                background: "transparent",
              }}
            ></div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              <label
                style={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "var(--ui-text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Date
              </label>
              <select
                style={{
                  border: "1px solid var(--ui-border)",
                  borderRadius: "6px",
                  padding: "9px 12px",
                  fontSize: "14px",
                  color: onLeave || isAbsent ? "var(--ui-text-muted)" : "var(--ui-text)",
                  backgroundColor: onLeave ? "var(--ui-surface-recessed)" : "white",
                  cursor: onLeave || isAbsent ? "not-allowed" : "pointer",
                  appearance: "none",
                  backgroundImage:
                    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E\")",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 12px center",
                  paddingRight: "36px",
                  transition: "all 0.15s ease",
                }}
                value={timeOutDate}
                onChange={handleTimeOutDateChange}
                disabled={onLeave || isAbsent}
              >
                {source === "employee-dtr" ? (
                  // For employee-dtr mode, show timeInDate and next day
                  <>
                    {timeInDate && (
                      <option value={timeInDate}>
                        {formatDisplayDate(timeInDate)}
                      </option>
                    )}
                    {nextDay && (
                      <option value={nextDay}>
                        {formatDisplayDate(nextDay)}
                      </option>
                    )}
                  </>
                ) : (
                  // For time-tracking mode, show selectedDate and next day
                  <>
                    {selectedDate && (
                      <option value={formatDateString(selectedDate)}>
                        {formatDisplayDate(selectedDate)}
                      </option>
                    )}
                    {nextDay && (
                      <option value={nextDay}>
                        {formatDisplayDate(nextDay)}
                      </option>
                    )}
                  </>
                )}
              </select>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              <label
                style={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "var(--ui-text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Time
              </label>
              {/* C2: the native time field (as the Work Schedule page). Disabled only
                  for Leave / Absent — never for edit mode, the main correction flow.
                  No outline override, so the app-wide :focus-visible ring shows. */}
              <input
                type="time"
                aria-label="Time Out"
                style={{
                  border: "1px solid var(--ui-border)",
                  borderRadius: "6px",
                  padding: "9px 12px",
                  fontSize: "14px",
                  fontVariantNumeric: "tabular-nums",
                  color: onLeave || isAbsent ? "var(--ui-text-muted)" : "var(--ui-text)",
                  background: onLeave || isAbsent ? "var(--ui-surface-recessed)" : "white",
                  fontFamily: "var(--font-family-primary)",
                  cursor: onLeave || isAbsent ? "not-allowed" : "text",
                }}
                value={toTimeInputValue(timeOutValue)}
                onChange={(e) => {
                  const next = e.target.value;
                  setTimeOutValue((prev) => to12h(next, prev.period));
                }}
                disabled={onLeave || isAbsent}
              />
              {errors.timeOut && (
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--ui-danger)",
                    marginTop: "0.25rem",
                  }}
                >
                  {errors.timeOut}
                </span>
              )}
            </div>
          </div>

          {/* Additional Details Section */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "14px",
              marginTop: "16px",
            }}
          >
            <div
              style={{
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            >
              <Icon name="settings" size={18} />
            </div>
            <div
              style={{
                fontSize: "13px",
                fontWeight: 600,
                color: "var(--ui-text)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Details
            </div>
            <div
              style={{
                flex: 1,
                height: "1px",
                background: "transparent",
              }}
            ></div>
          </div>

          {/* Attendance Status Selector */}
          <div
            style={{
              background: "var(--ui-bg)",
              border: "1px solid var(--ui-border)",
              borderRadius: "8px",
              padding: "14px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                fontSize: "11px",
                fontWeight: 600,
                color: "var(--ui-text-muted)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: "10px",
              }}
            >
              Attendance Status
            </div>
            <div
              style={{
                display: "flex",
                gap: "8px",
              }}
            >
              {/* Worked Option */}
              <label
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  padding: "10px 12px",
                  border: getAttendanceStatus() === 'worked' ? "2px solid var(--ui-success)" : "2px solid var(--ui-border)",
                  borderRadius: "8px",
                  background: getAttendanceStatus() === 'worked' ? "var(--ui-success-tint)" : "white",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  opacity: 1,
                }}
                onClick={() => handleAttendanceStatusChange('worked')}
              >
                <span><Icon name="check-circle" size={16} /></span>
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: getAttendanceStatus() === 'worked' ? "var(--ui-text)" : "var(--ui-text-secondary)",
                  }}
                >
                  Worked
                </span>
              </label>

              {/* Leave Option */}
              <label
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  padding: "10px 12px",
                  border: getAttendanceStatus() === 'leave' ? "2px solid var(--ui-accent)" : "2px solid var(--ui-border)",
                  borderRadius: "8px",
                  background: getAttendanceStatus() === 'leave' ? "var(--ui-warning-tint)" : "white",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  opacity: 1,
                }}
                onClick={() => handleAttendanceStatusChange('leave')}
              >
                <span><Icon name="umbrella" size={16} /></span>
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: getAttendanceStatus() === 'leave' ? "var(--ui-text)" : "var(--ui-text-secondary)",
                  }}
                >
                  Leave
                </span>
              </label>

              {/* Absent Option */}
              <label
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  padding: "10px 12px",
                  border: getAttendanceStatus() === 'absent' ? "2px solid var(--ui-danger)" : "2px solid var(--ui-border)",
                  borderRadius: "8px",
                  background: getAttendanceStatus() === 'absent' ? "var(--ui-danger-tint)" : "white",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  opacity: 1,
                }}
                onClick={() => handleAttendanceStatusChange('absent')}
              >
                <span><Icon name="x-circle" size={16} /></span>
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: getAttendanceStatus() === 'absent' ? "var(--ui-text)" : "var(--ui-text-secondary)",
                  }}
                >
                  Absent
                </span>
              </label>
            </div>

            {/* Leave sub-options - only show when Leave is selected */}
            {onLeave && (
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  marginTop: "10px",
                  paddingLeft: "4px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <input
                    type="radio"
                    id="paid-leave"
                    name="leaveType"
                    checked={isPaidLeave}
                    onChange={() => setIsPaidLeave(true)}
                    style={{
                      width: "16px",
                      height: "16px",
                      accentColor: "var(--ui-accent)",
                      cursor: "pointer",
                    }}
                  />
                  <label
                    htmlFor="paid-leave"
                    style={{
                      fontSize: "13px",
                      color: "var(--ui-text-secondary)",
                      cursor: "pointer",
                    }}
                  >
                    Paid Leave
                  </label>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <input
                    type="radio"
                    id="unpaid-leave"
                    name="leaveType"
                    checked={!isPaidLeave}
                    onChange={() => setIsPaidLeave(false)}
                    style={{
                      width: "16px",
                      height: "16px",
                      accentColor: "var(--ui-accent)",
                      cursor: "pointer",
                    }}
                  />
                  <label
                    htmlFor="unpaid-leave"
                    style={{
                      fontSize: "13px",
                      color: "var(--ui-text-secondary)",
                      cursor: "pointer",
                    }}
                  >
                    Unpaid Leave
                  </label>
                </div>
              </div>
            )}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              <label
                style={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "var(--ui-text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Break Time
              </label>
              <select
                style={{
                  border: "1px solid var(--ui-border)",
                  borderRadius: "6px",
                  padding: "9px 12px",
                  fontSize: "14px",
                  color: onLeave || isAbsent || isPieceRate ? "var(--ui-text-muted)" : "var(--ui-text)",
                  backgroundColor: onLeave || isAbsent || isPieceRate ? "var(--ui-surface-recessed)" : "white",
                  cursor: onLeave || isAbsent || isPieceRate ? "not-allowed" : "pointer",
                  appearance: "none",
                  backgroundImage:
                    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E\")",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 12px center",
                  paddingRight: "36px",
                  transition: "all 0.15s ease",
                }}
                value={breakMinutes}
                onChange={(e) => setBreakMinutes(parseInt(e.target.value))}
                disabled={onLeave || isAbsent || isPieceRate}
              >
                <option value={0}>0 minutes</option>
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>60 minutes (1 hour)</option>
                <option value={90}>90 minutes (1.5 hours)</option>
                <option value={120}>120 minutes (2 hours)</option>
              </select>
              {errors.break && (
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--ui-danger)",
                    marginTop: "0.25rem",
                  }}
                >
                  {errors.break}
                </span>
              )}
            </div>

            {/* Empty div for grid alignment */}
            <div></div>

            {/* Piece Rate Section */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                gridColumn: "1 / -1",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  padding: "10px 0",
                }}
              >
                {/* Checkbox and label */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    minWidth: "120px",
                  }}
                >
                  <input
                    type="checkbox"
                    id="piece-rate-checkbox"
                    checked={isPieceRate}
                    onChange={handlePieceRateChange}
                    disabled={onLeave || isAbsent}
                    style={{
                      width: "18px",
                      height: "18px",
                      accentColor: "var(--ui-danger)",
                      cursor: onLeave || isAbsent ? "not-allowed" : "pointer",
                      opacity: onLeave || isAbsent ? 0.5 : 1,
                    }}
                  />
                  <label
                    htmlFor="piece-rate-checkbox"
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: onLeave || isAbsent ? "var(--ui-text-faint)" : "var(--ui-text-secondary)",
                      cursor: onLeave || isAbsent ? "not-allowed" : "pointer",
                      userSelect: "none",
                      opacity: onLeave || isAbsent ? 0.5 : 1,
                    }}
                  >
                    Piece Rate
                  </label>
                </div>

                {/* Amount input */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flex: 1,
                    opacity: !isPieceRate ? 0.5 : 1,
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "var(--ui-text-secondary)",
                    }}
                  >
                    ₱
                  </span>
                  <input
                    type="text"
                    style={{
                      flex: 1,
                      maxWidth: "150px",
                      border: errors.pieceRate ? "1px solid var(--ui-danger)" : "1px solid var(--ui-border)",
                      borderRadius: "6px",
                      padding: "9px 12px",
                      fontSize: "14px",
                      color: "var(--ui-text)",
                      background: !isPieceRate ? "var(--ui-surface-recessed)" : "white",
                      fontVariantNumeric: "tabular-nums",
                      transition: "all 0.15s ease",
                      cursor: !isPieceRate ? "not-allowed" : "text",
                    }}
                    placeholder="0.00"
                    value={pieceRateValue}
                    onChange={handlePieceRateValueChange}
                    disabled={!isPieceRate}
                  />
                  <span
                    style={{
                      fontSize: "11px",
                      color: errors.pieceRate ? "var(--ui-danger)" : "var(--ui-text-muted)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {errors.pieceRate || "(₱100 - ₱10,000)"}
                  </span>
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                gridColumn: "1 / -1",
              }}
            >
              <label
                style={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "var(--ui-text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Notes (Optional)
              </label>
              <textarea
                style={{
                  border: "1px solid var(--ui-border)",
                  borderRadius: "6px",
                  padding: "10px 12px",
                  fontSize: "13px",
                  color: "var(--ui-text)",
                  background: "white",
                  fontFamily: "var(--font-family-primary)",
                  resize: "vertical",
                  minHeight: "56px",
                  transition: "all 0.15s ease",
                  cursor: "text",
                }}
                placeholder="Add any relevant notes..."
                value={notes}
                onChange={handleNotesChange}
                rows={2}
              />
              <span
                style={{
                  fontSize: "11px",
                  color: "var(--ui-text-faint)",
                  textAlign: "right",
                  marginTop: "4px",
                }}
              >
                {notes.length}/100 characters
              </span>
            </div>
          </div>

          {/* Warning Banner for Piece Rate */}
          {isPieceRate && (
            <div
              style={{
                background: "var(--ui-accent-tint)",
                border: "1px solid var(--ui-accent)",
                borderRadius: "8px",
                padding: "10px 12px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginTop: "12px",
              }}
            >
              <span><Icon name="alert-triangle" size={16} /></span>
              <span
                style={{
                  fontSize: "13px",
                  color: "var(--ui-warning-text)",
                  fontWeight: 500,
                }}
              >
                {holiday
                  ? "Piece rate active: Overtime, night differential, and holiday premium will not be calculated"
                  : "Piece rate active: Overtime and night differential will not be calculated"}
              </span>
            </div>
          )}

          {/* Info Banner for Paid Leave */}
          {onLeave && isPaidLeave && (
            <div
              style={{
                background: "var(--ui-warning-tint)",
                border: "1px solid var(--ui-accent)",
                borderRadius: "8px",
                padding: "10px 12px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginTop: "12px",
              }}
            >
              <span><Icon name="check-circle" size={16} /></span>
              <span
                style={{
                  fontSize: "13px",
                  color: "var(--ui-warning-text)",
                  fontWeight: 500,
                }}
              >
                Paid leave: Employee IS entitled to holiday pay if a holiday falls after this day
              </span>
            </div>
          )}

          {/* Info Banner for Unpaid Leave */}
          {onLeave && !isPaidLeave && (
            <div
              style={{
                background: "var(--ui-danger-tint)",
                border: "1px solid var(--ui-danger)",
                borderRadius: "8px",
                padding: "10px 12px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginTop: "12px",
              }}
            >
              <span><Icon name="alert-triangle" size={16} /></span>
              <span
                style={{
                  fontSize: "13px",
                  color: "var(--ui-danger-text)",
                  fontWeight: 500,
                }}
              >
                Unpaid leave: Employee is NOT entitled to holiday pay if a holiday falls after this day
              </span>
            </div>
          )}

          {/* Info Banner for Absent */}
          {isAbsent && (
            <div
              style={{
                background: "var(--ui-danger-tint)",
                border: "1px solid var(--ui-danger)",
                borderRadius: "8px",
                padding: "10px 12px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginTop: "12px",
              }}
            >
              <span><Icon name="alert-triangle" size={16} /></span>
              <span
                style={{
                  fontSize: "13px",
                  color: "var(--ui-danger-text)",
                  fontWeight: 500,
                }}
              >
                Absent: Employee is NOT entitled to holiday pay if a holiday falls after this day
              </span>
            </div>
          )}

          {/* Undertime Warning Banner */}
          {!onLeave && !isAbsent && !isPieceRate && calculations.undertime > 0 && (
            <div
              style={{
                background: "var(--ui-danger-tint)",
                border: "1px solid var(--ui-danger)",
                borderRadius: "8px",
                padding: "10px 12px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginTop: "12px",
              }}
            >
              <span><Icon name="clock" size={16} /></span>
              <span
                style={{
                  fontSize: "13px",
                  color: "var(--ui-danger-text)",
                  fontWeight: 500,
                }}
              >
                Undertime: {calculations.undertime.toFixed(2)} hrs deduction will apply
              </span>
            </div>
          )}

          {/* Payroll Rates Error Banner */}
          {payrollRatesError && (
            <div
              style={{
                background: "var(--ui-warning-tint)",
                border: "1px solid var(--ui-warning)",
                borderRadius: "8px",
                padding: "10px 12px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginTop: "12px",
              }}
            >
              <span><Icon name="alert-triangle" size={16} /></span>
              <span
                style={{
                  fontSize: "13px",
                  color: "var(--ui-warning-text)",
                  fontWeight: 500,
                }}
              >
                {payrollRatesError}
              </span>
            </div>
          )}

          {/* Stats Section */}
          <div
            className="calc-stats"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: "10px",
              background: "var(--ui-bg)",
              border: "1px solid var(--ui-border)",
              borderRadius: "8px",
              padding: "14px",
              marginBottom: "16px",
              marginTop: "8px",
            }}
          >
            <div
              style={{
                textAlign: "center",
                padding: "8px",
                background: "white",
                borderRadius: "6px",
                border: "1px solid var(--ui-border)",
              }}
            >
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: 600,
                  color: "var(--ui-text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "4px",
                }}
              >
                Holiday
              </div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                  color: holiday ? "var(--ui-success)" : "var(--ui-text)",
                }}
              >
                {holiday ? "YES" : "NO"}
              </div>
            </div>
            <div
              style={{
                textAlign: "center",
                padding: "8px",
                background: "white",
                borderRadius: "6px",
                border: "1px solid var(--ui-border)",
              }}
            >
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: 600,
                  color: "var(--ui-text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "4px",
                }}
              >
                Overtime
              </div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                  color: isPieceRate ? "var(--ui-text-faint)" : "var(--ui-accent)",
                  textDecoration: isPieceRate ? "line-through" : "none",
                }}
              >
                {calculations.overtime.toFixed(2)}
              </div>
            </div>
            <div
              style={{
                textAlign: "center",
                padding: "8px",
                background: "white",
                borderRadius: "6px",
                border: "1px solid var(--ui-border)",
              }}
            >
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: 600,
                  color: "var(--ui-text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "4px",
                }}
              >
                Night Diff
              </div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                  color: isPieceRate ? "var(--ui-text-faint)" : "var(--ui-primary)",
                  textDecoration: isPieceRate ? "line-through" : "none",
                }}
              >
                {calculations.nightDiff.toFixed(2)}
              </div>
            </div>
            <div
              style={{
                textAlign: "center",
                padding: "8px",
                background: "white",
                borderRadius: "6px",
                border: "1px solid var(--ui-border)",
              }}
            >
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: 600,
                  color: "var(--ui-text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "4px",
                }}
              >
                Undertime
              </div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                  color: isPieceRate ? "var(--ui-text-faint)" : (calculations.undertime > 0 ? "var(--ui-danger)" : "var(--ui-text)"),
                  textDecoration: isPieceRate ? "line-through" : "none",
                }}
              >
                {calculations.undertime.toFixed(2)}
              </div>
            </div>
            <div
              style={{
                textAlign: "center",
                padding: "8px",
                background: "white",
                borderRadius: "6px",
                border: "1px solid var(--ui-border)",
              }}
            >
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: 600,
                  color: "var(--ui-text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "4px",
                }}
              >
                Late
              </div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                  color: isPieceRate ? "var(--ui-text-faint)" : (calculations.late > 0 ? "var(--ui-accent)" : "var(--ui-text)"),
                  textDecoration: isPieceRate ? "line-through" : "none",
                }}
              >
                {calculations.late.toFixed(2)}
              </div>
            </div>
            {isPieceRate && pieceRateValue && (
              <div
                style={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  padding: "8px",
                  background: "white",
                  borderRadius: "6px",
                  border: "1px solid var(--ui-border)",
                }}
              >
                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: 600,
                    color: "var(--ui-text-muted)",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    marginBottom: "4px",
                  }}
                >
                  Piece Rate Amount
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 700,
                    fontVariantNumeric: "tabular-nums",
                    color: "var(--ui-danger)",
                  }}
                >
                  ₱{parseFloat(pieceRateValue).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ND Break Ambiguity Warning */}
        {showNDBreakWarning && calculations.nightDiff > 0 && (
          <div
            style={{
              margin: "0 24px 16px 24px",
              padding: "12px 16px",
              background: "var(--ui-accent-tint)",
              border: "1px solid var(--ui-accent)",
              borderRadius: "8px",
              display: "flex",
              gap: "10px",
              alignItems: "flex-start",
            }}
          >
            <span style={{ flexShrink: 0 }}><Icon name="alert-triangle" size={16} /></span>
            <div style={{ fontSize: "13px", color: "var(--ui-warning-text)", lineHeight: "1.5" }}>
              <strong>Night Diff Note:</strong> This shift spans both ND ({effectivePolicy?.nightDiffStart || 22}:00-{effectivePolicy?.nightDiffEnd || 6}:00) and non-ND hours.
              The system assumes break was taken during non-ND time, maximizing ND pay.
            </div>
          </div>
        )}

        <div
          style={{
            padding: "14px 24px",
            borderTop: "1px solid var(--ui-border)",
            background: "var(--ui-bg)",
            borderRadius: "0 0 12px 12px",
            flexShrink: 0,
          }}
        >
          {/* Soft Lock Warning — informational only, does not block saves */}
          {mode === "edit" && lockStatus === "draft" && (
            <div
              style={{
                padding: "0.625rem 0.75rem",
                backgroundColor: "var(--ui-warning-tint)",
                border: "1px solid var(--ui-warning)",
                borderRadius: "8px",
                fontSize: "0.8125rem",
                marginBottom: "12px",
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
                lineHeight: "1.4",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ui-warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "1px" }}>
                <line x1="12" y1="5" x2="12" y2="15" />
                <circle cx="12" cy="3" r="2" />
                <path d="M6 15h12l-1 5H7z" />
              </svg>
              <span style={{ color: "var(--ui-warning-text)" }}>
                This record is referenced by <strong>Draft {lockRunId}</strong>. Changes will require the draft's gross income to be recalculated.
              </span>
            </div>
          )}

          {/* Error Message */}
          {errors.save && (
            <div
              style={{
                padding: "0.75rem",
                backgroundColor: "var(--ui-danger-tint)",
                color: "var(--ui-danger)",
                borderRadius: "8px",
                fontSize: "0.875rem",
                marginBottom: "12px",
              }}
            >
              {errors.save}
            </div>
          )}

          {/* Button Container */}
          {isHardLocked ? (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                style={{
                  padding: "10px 24px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  border: "1px solid var(--ui-border)",
                  background: "white",
                  color: "var(--ui-text-secondary)",
                }}
                onClick={handleClose}
              >
                Close
              </button>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {/* Left side - Delete button (only in edit mode) */}
              <div>
                {mode === "edit" && (
                  <button
                    style={{
                      padding: "10px 24px",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: 600,
                      cursor: saving ? "not-allowed" : "pointer",
                      transition: "all 0.15s ease",
                      border: "1px solid var(--ui-danger)",
                      background: "white",
                      color: "var(--ui-danger)",
                      opacity: saving ? 0.5 : 1,
                    }}
                    onClick={handleDelete}
                    disabled={saving}
                    onMouseEnter={(e) => {
                      if (!saving) {
                        e.target.style.background = "var(--ui-danger-tint)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "white";
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>

              {/* Right side - Cancel and Save/Update buttons */}
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  style={{
                    padding: "10px 24px",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: saving ? "not-allowed" : "pointer",
                    transition: "all 0.15s ease",
                    border: "1px solid var(--ui-border)",
                    background: "white",
                    color: "var(--ui-text-secondary)",
                    opacity: saving ? 0.5 : 1,
                  }}
                  onClick={handleClose}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  style={{
                    padding: "10px 24px",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor:
                      saving || loadingEmployees || isAddWaitingOnLocks || isAddBlocked ? "not-allowed" : "pointer",
                    transition: "all 0.15s ease",
                    border: "none",
                    background: isRestDay ? "var(--ui-warning-hover)" : "var(--ui-primary)",
                    color: "white",
                    opacity: saving || loadingEmployees || isAddWaitingOnLocks || isAddBlocked ? 0.5 : 1,
                  }}
                  onClick={handleSave}
                  disabled={saving || loadingEmployees || isAddWaitingOnLocks || isAddBlocked}
                >
                  {saving
                    ? mode === "edit"
                      ? "Updating..."
                      : "Saving..."
                    : mode === "edit"
                    ? "Update Record"
                    : "Save Record"}
                </button>
              </div>
            </div>
          )}
        </div>

      {/* Delete Confirmation Modal — stacked on this one (C7) */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Time Record"
        message="Are you sure you want to delete this time record? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </Modal>
  );
};

export default AddTimeRecordModal;
